import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Box, Typography, alpha } from '@mui/material';
import { mapChartConfig, formatChartData } from './chartConfigMapper';

const SmartCardChart = ({ config, color = '#667eea', domain = null }) => {
  if (!config) {
    return null;
  }

  // Map backend config to Recharts format
  const mappedConfig = mapChartConfig(config, domain);
  if (!mappedConfig || !mappedConfig.data || mappedConfig.data.length === 0) {
    return null;
  }

  const chartType = mappedConfig.type?.toLowerCase() || 'line';
  const data = formatChartData(mappedConfig.data, mappedConfig);
  const chartColor = mappedConfig.color || color;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: 'rgba(26, 29, 46, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            p: 1.5,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          }}
        >
          <Typography variant="caption" sx={{ color: '#E0E0E0', fontWeight: 600 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={`item-${index}`}
              variant="caption"
              sx={{ color: entry.color, display: 'block', mt: 0.5 }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Line Chart
  if (chartType === 'line') {
    return (
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <defs>
            <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColor} stopOpacity={0.8} />
              <stop offset="95%" stopColor={chartColor} stopOpacity={0.2} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
          <XAxis
            dataKey={mappedConfig.xAxisKey || "name"}
            stroke="rgba(255, 255, 255, 0.3)"
            style={{ fontSize: '0.7rem' }}
            tick={{ fill: 'rgba(255, 255, 255, 0.5)' }}
          />
          <YAxis
            stroke="rgba(255, 255, 255, 0.3)"
            style={{ fontSize: '0.7rem' }}
            tick={{ fill: 'rgba(255, 255, 255, 0.5)' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey={mappedConfig.yAxisKey || "value"}
            stroke={chartColor}
            strokeWidth={mappedConfig.strokeWidth || 3}
            dot={mappedConfig.dot !== false ? { fill: chartColor, r: 4 } : false}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // Area Chart
  if (chartType === 'area') {
    return (
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <defs>
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColor} stopOpacity={0.6} />
              <stop offset="95%" stopColor={chartColor} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
          <XAxis
            dataKey={mappedConfig.xAxisKey || "name"}
            stroke="rgba(255, 255, 255, 0.3)"
            style={{ fontSize: '0.7rem' }}
            tick={{ fill: 'rgba(255, 255, 255, 0.5)' }}
          />
          <YAxis
            stroke="rgba(255, 255, 255, 0.3)"
            style={{ fontSize: '0.7rem' }}
            tick={{ fill: 'rgba(255, 255, 255, 0.5)' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey={mappedConfig.yAxisKey || "value"}
            stroke={chartColor}
            strokeWidth={mappedConfig.strokeWidth || 2}
            fill="url(#areaGradient)"
            fillOpacity={mappedConfig.fillOpacity || 0.6}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // Bar Chart
  if (chartType === 'bar' || chartType === 'stack bar') {
    return (
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
          <XAxis
            dataKey="name"
            stroke="rgba(255, 255, 255, 0.3)"
            style={{ fontSize: '0.7rem' }}
            tick={{ fill: 'rgba(255, 255, 255, 0.5)' }}
          />
          <YAxis
            stroke="rgba(255, 255, 255, 0.3)"
            style={{ fontSize: '0.7rem' }}
            tick={{ fill: 'rgba(255, 255, 255, 0.5)' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar 
            dataKey={mappedConfig.yAxisKey || "value"} 
            fill={chartColor} 
            radius={[8, 8, 0, 0]}
            barSize={mappedConfig.barSize || 40}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Donut/Pie Chart
  if (chartType === 'donut' || chartType === 'pie') {
    // Use colors from config if available, otherwise generate from base color
    const COLORS = mappedConfig.colors && mappedConfig.colors.length > 0
      ? mappedConfig.colors
      : [chartColor, alpha(chartColor, 0.7), alpha(chartColor, 0.5), alpha(chartColor, 0.3)];
    
    // Custom label renderer for better visibility
    const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text
          x={x}
          y={y}
          fill="#E0E0E0"
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
          fontSize="11"
          fontWeight="500"
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    // Calculate total for percentage display
    const total = data.reduce((sum, entry) => sum + (entry.value || 0), 0);
    
    return (
      <Box>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={chartType === 'donut' ? (mappedConfig.innerRadius || 50) : 0}
              outerRadius={mappedConfig.outerRadius || 70}
              fill={chartColor}
              dataKey={mappedConfig.valueKey || "value"}
              nameKey={mappedConfig.labelKey || "name"}
              paddingAngle={2}
              label={renderLabel}
              labelLine={false}
            >
              {data.map((entry, index) => {
                // Use color from entry if available, otherwise use COLORS array
                const fillColor = entry.color || COLORS[index % COLORS.length];
                return <Cell key={`cell-${index}`} fill={fillColor} />;
              })}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        
        {/* Legend below chart */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1.5,
            mt: 2,
            px: 1,
          }}
        >
          {data.map((entry, index) => {
            const fillColor = entry.color || COLORS[index % COLORS.length];
            const percentage = total > 0 ? ((entry.value || 0) / total * 100).toFixed(1) : 0;
            return (
              <Box
                key={`legend-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '2px',
                    bgcolor: fillColor,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#E0E0E0',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  }}
                >
                  {entry.name}: {percentage}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Default: Simple stats display
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        py: 2,
        bgcolor: alpha(chartColor, 0.1),
        borderRadius: '12px',
        border: `1px solid ${alpha(chartColor, 0.2)}`,
      }}
    >
      {data.slice(0, 3).map((item, index) => (
        <Box key={index} textAlign="center">
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: chartColor,
              fontSize: '1.5rem',
            }}
          >
            {item.value}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.7rem',
            }}
          >
            {item.name}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default SmartCardChart;
